// checkout/utils/populateForm.ts

import { decodeHtml } from '../../utils/decodeHtml';
import { checkoutData } from '../init';

/**
 * Populates a form's fields based on a data object.
 *
 * @param {string} formId - The ID of the form element to populate.
 * @param {Record<string, any>} data - An object where each key corresponds to a field name and the value to assign.
 * @param {Record<string, (string | { value: string; text: string })[]>} [dynamicOptions]
 *        - Optional map of field names to arrays of option labels or { value, text } pairs (used for <select>).
 */
export function populateForm(
	formId: string,
	data: Record<string, any>,
	dynamicOptions?: Record<string, (string | { value: string; text: string })[]>,
): void {
	const form = document.getElementById(formId) as HTMLFormElement | null;
	if (!form) {
		console.warn(`Form with ID '${formId}' not found.`);
		return;
	}

	Object.entries(data).forEach(([fieldName, value]) => {
		const field = form.elements.namedItem(fieldName) as
			| HTMLInputElement
			| HTMLSelectElement
			| HTMLTextAreaElement
			| null;

		if (!field) return;

		if (field instanceof HTMLInputElement && field.type === 'checkbox') {
			field.checked = value === true || value === 'true';
		} else if (field instanceof HTMLSelectElement && dynamicOptions?.[fieldName]) {
			const optgroup = field.querySelector('optgroup') || field;
			optgroup.innerHTML = '';
			dynamicOptions[fieldName].forEach((opt) => {
				const option = document.createElement('option');
				if (typeof opt === 'string') {
					option.value = opt;
					option.textContent = decodeHtml(opt);
				} else {
					option.value = opt.value;
					option.textContent = decodeHtml(opt.text);
				}
				if (option.value === value) option.selected = true;
				optgroup.appendChild(option);
			});
		} else {
			field.value = value != null ? String(value) : '';
		}
	});

	form.querySelectorAll<HTMLElement>('[data-name]').forEach((el) => {
		const keys = el.dataset.name?.split(' ') || [];
		const parts = keys.map((key) => data[key]).filter((v) => v != null);
		el.textContent = parts.join(' ');
	});

	document.querySelectorAll<HTMLElement>('[data-name]').forEach((el) => {
		if (!form.contains(el)) {
			const keys = el.dataset.name?.split(' ') || [];
			const parts = keys
				.map((key) =>
					Object.prototype.hasOwnProperty.call(data, key) ? data[key] : undefined,
				)
				.filter((v) => v != null && v !== '');
			if (parts.length > 0) {
				el.textContent = parts.join(' ');
			}
		}
	});

	if (formId === 'paymentMethodForm') {
		const addressContainer = document.getElementById('summary-address');
		if (addressContainer) {
			const spans = Array.from(addressContainer.querySelectorAll<HTMLElement>('[data-name]'));
			const populated = spans.filter((span) => span.textContent && span.textContent.trim());
			addressContainer.innerHTML = '';
			populated.forEach((span, i) => {
				addressContainer.appendChild(span);
				if (i < populated.length - 1) {
					addressContainer.appendChild(document.createTextNode(', '));
				}
			});
		}
	}

	if (checkoutData.login?.redPoints > 0) {
		const pointsTotal = document.querySelector('#virginPointsTotal') as HTMLElement | null;
		const orderSummaryPoints = document.querySelector(
			'.order-summary__item--virgin-points',
		) as HTMLElement | null;
		if (pointsTotal) {
			pointsTotal.innerHTML = '+' + checkoutData.login?.redPoints + ' points';
		}
		orderSummaryPoints?.removeAttribute('hidden');
	}

	if (checkoutData.accountDetails?.redPoints > 0) {
		const pointsTotal = document.querySelector('#virginPointsTotal') as HTMLElement | null;
		const orderSummaryPoints = document.querySelector(
			'.order-summary__item--virgin-points',
		) as HTMLElement | null;
		if (pointsTotal) {
			pointsTotal.innerHTML = '+' + checkoutData.accountDetails?.redPoints + ' points';
		}
		orderSummaryPoints?.removeAttribute('hidden');
	}

	if (formId === 'registrationForm') {
		if (checkoutData.register?.showConsent == 'true') {
			form.querySelector('#registerForm-showConsent')?.removeAttribute('hidden');
		}
	}

	if (formId === 'accountDetailsForm') {
		if (checkoutData.accountDetails?.voucherConflict == 'true') {
			const vcModal = document.querySelector('wc-voucher-conflict-modal') as HTMLElement;
			if (!vcModal) {
				const vcModalNew = document.createElement('wc-voucher-conflict-modal');
				const vcDiscount =
					checkoutData.accountDetails?.currentlyAttachedVoucher.promotionVoucher.promotion
						.discountAmount;
				const vcDiscountType =
					checkoutData.accountDetails?.currentlyAttachedVoucher.promotionVoucher.promotion
						.discountType;
				let vcDiscountFormatted: string;

				if (vcDiscountType === 'amount_off') {
					const discount = parseFloat(vcDiscount);
					vcDiscountFormatted = '£' + discount.toFixed(2);
				} else if (vcDiscountType === 'percentage_off') {
					vcDiscountFormatted = vcDiscount + '%';
				} else {
					vcDiscountFormatted = '';
				}

				const variables = {
					voucherAmount: vcDiscountFormatted,
					voucherCode:
						checkoutData.accountDetails?.currentlyAttachedVoucher.promotionVoucher.code,
					voucherType: vcDiscountType,
				};
				vcModalNew.setAttribute('data-variables', JSON.stringify(variables));
				document.body.appendChild(vcModalNew);
			}
		}
	}
}
